<template>
    <div v-show="!isContentReady" class='tep-loader-wrap-outer'>
        <div class='tep-loader-wrap-inner'>
            <div class="absolute top-1/2 left-[45%]"><dx-loader /></div>
        </div>
    </div>
    <div class='tep-cards-view-section'>
        <universal-form v-if='!!cardMeta'
            ref='cardForm'
            :meta='cardMeta'
            :use-default-buttons="true"
            :toRemoveToolbarItemsNames="['choice', 'edit', 'delete', 'copy']"
            components-path="views/tep/tep-grid/components"
            :callbacks="{refreshData: refreshTepCardData, saveData: saveTepCardData, isDirty: null}"
        />
        <div
            v-if="isWidgest"
            class="tep-cards-view-widget"
            :class="{ 'isClickTrue' : cardForm?.serverData.kod }"
        >
            <widgets-side-menu local-storage-key="avCardWidgetList" />
            <widgets-popup
                ref="widgetPopupRef"
                :user-data="cardForm?.serverData"
            />
        </div>
    </div>
</template>

<script>
export default {
    name: 'view-tep-cards',
};
</script>

<script setup>

import { computed, provide, ref, watch } from 'vue';
import { useStore } from 'vuex';
import DxLoader from '@/components/loader/dx-loader.vue';
import UniversalForm from '@/components/card-component/card-form.vue';
import getCardMeta from '@/views/tep/tep-grid/store/cards-meta';
import getCardData from '@/views/tep/tep-grid/store/cards-data';
import { dxUiNotify } from '@/utils/dx-helper';
import tepService from '@/service/tep-service';
import store from './store/tep-tree-store';
import tepCardsStore from '@/views/tep/tep-grid/store/tep-cards-store';
import AiCustomConfirmDialog from '@/utils/ai-custom-confirm-dialog';
import WidgetsSideMenu from '@/components/card-component/widgets-side-menu.vue';
import useCardWidgetList from '@/views/tep/tep-grid/components/widgets/widget-list';
import WidgetsPopup from '@/components/card-component/widgets-popup.vue';

const userData = ref({});
const isContentReady = ref(true);
const cardMeta = ref(null);
const currentProps = ref({});
const cardForm = ref(null);
const widgetPopupRef = ref(null);
const isWidgest = ref(false);

provide('userData', userData);
const storeX = useStore();

const emit = defineEmits(['changeTreeData', 'createTreeData'])

const isWidgestChangeData = computed(() => {
    return tepCardsStore.state.isChangeWidget;
})

watch(isWidgestChangeData, (newValue) => {
    if(newValue === 'isWidgetChange') {
        cardForm.value.onRefreshDataButtonClick();
        // storeX.commit('clearOpenedWidget');
    }
});

/** Функция, с которой все начинается (как onMounted) */
const changeCard = async (itemData) => {
    let pvName;
    if(itemData.kod_vid === 55) {
        pvName = await tepService.getWidgetPvName({kod_sx: itemData.kod});
    }
    const widgetList = useCardWidgetList(itemData.kod_vid, pvName);
    isWidgest.value = Boolean(widgetList.length);
    storeX.dispatch('createWidgetMetaList', widgetList);
    if(itemData.kod !== currentProps.value.kod) {
        storeX.commit('clearOpenedWidget');
    }
    // проверка, что предыдущая карточка (если она была) сохранена
    if (cardForm.value?.isDirtyController()) {
        const result = await AiCustomConfirmDialog('У вас есть несохраненные изменения, сохранить изменения?');
        if (result === 1) { // нажали "ДА"
            isContentReady.value = false;
            const res = await cardForm.value?.onSaveDataButtonClick();
            isContentReady.value = true;
            if (!res) return;
        } else if (result === 0) { // нажали "НЕТ"
            await cardForm.value?.clearUserData();
        } else { // нажали "ОТМЕНА"
            return;
        }
    }
    isContentReady.value = false;
    cardMeta.value = null;
    const cardName = await tepService.getCardVidName(itemData.kod_vid);
    if (itemData.kod) {
        currentProps.value = {
            kod_vid: itemData.kod_vid,
            kod_r: itemData.kod_r,
            kod: itemData.kod,
            cardName: cardName,
            itemName: itemData.name
        };
    } else {
        currentProps.value = {
            kod_vid: itemData.kod_vid,
            kod_r: itemData.kod_r,
            cardName: cardName,
            new: true
        };
    }
    cardMeta.value = await getCardMeta(currentProps.value);
    isContentReady.value = true;
};

const clearCard = (itemKod) => {
    if (currentProps.value.kod === itemKod) {
        cardMeta.value = null;
        currentProps.value = {};
        isWidgest.value = false;
    }
}

const validateForm = (data) => {
    if (!data.name || (data.name && data.name.replaceAll(' ', '').length === 0)) {
        return {errors: {message: 'Поле "Наименование" не может быть пустым'}}
    }
    if ([40, 41, 42, 45, 46].includes(currentProps.value.kod_vid) && isNaN(Number(data.nn))) {
        return {errors: {message: 'Поле "№" (номер) должно содержать только цифры'}}
    }
    if ([22550000].includes(currentProps.value.kod_vid) && (!data.kod_ctp || isNaN(parseInt(data.kod_ctp)))) {
        return {errors: {message: 'Поле "Тип ЦТП" является обязательным. Выберите тип ЦТП из справочника!'}}
    }
    return 'ok';
}

const refreshTepCardData = async () => {
    tepCardsStore.changeWidget(false);
    if (currentProps.value.new) {
        return await getCardData(currentProps.value);
    } else {
        const data = await tepService.getCardVidData(currentProps.value.kod, currentProps.value.kod_vid);
        if (!data) { // значит вернулся null
            return await getCardData(currentProps.value);
        }
        return {result: true, success: true, data};
    }
}

const saveTepCardData = async (data) => {
    const validateRes = validateForm(data);
    if (validateRes !== 'ok') return validateRes;
    isContentReady.value = false;
    if(!tepCardsStore.state.statusSaveButton) {
        tepCardsStore.changeSavegrid(true);
        await tepCardsStore.waitForGridSave();
    }
    if (currentProps.value.new) {
        const res = await store.createTreeItem({...currentProps.value, ...data}, currentProps.value.kod_r);
        if (res[0].result) {
            const currProps = currentProps.value;
            currProps.new = false;
            currProps.kod = res[1];
            currentProps.value = { ...currProps };
            emit('createTreeData', currProps.kod);
            dxUiNotify('Элемент ТС успешно создан', 'success', 3000);
        }

        isContentReady.value = true;
        return res[0];
    } else {
        const res = await store.updateTreeItem({...currentProps.value, ...data});
        if (res.result) {
            emit('changeTreeData', ['kod', currentProps.value.kod]);
            tepCardsStore.changeWidget('isCardChange');
            dxUiNotify('Элемент ТС успешно обновлен', 'success', 3000);
        }
        isContentReady.value = true;
        return res;
    }
}

defineExpose({
    changeCard,
    clearCard,
});
</script>

<style scoped>
:deep(.card-inner) {
    margin-bottom: 50px;
    width: 100%;
}

:deep(.card-inner .ai-card-scroll-panel > .dx-scrollable-wrapper > .dx-scrollable-container) {
    padding-right: 30px;
}

.tep-cards-view-section {
    display: flex;
    height: 100%;
}
.tep-cards-view-widget {
    background: #f6fafe;
    border-radius: 12px;
    pointer-events: none;
    opacity: 0.6;
    margin-left: auto;
}

.tep-cards-view-widget.isClickTrue {
    pointer-events: auto;
    opacity: 1;
}

.tep-cards-view-widget :deep(.dx-list-item-content::before) {
    display: none;
}

.tep-cards-view-widget :deep(.dx-list-item > .flex) {
    font-size: 13px;
    padding: 10px 11px 9px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
}
</style>
